import React, { useState } from 'react';

import { AutoComplete } from 'primereact/autocomplete';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { AddContact } from 'modules/cases/addContactModal';
import ImageComponent from 'shared/ImageComponent';

import { generateFullName } from 'utils/generateFullNameUtils';
import { Controller } from 'react-hook-form';
import { PI_PROVIDERS_TYPE } from 'constants/index';
import { useLeadContext } from 'modules/lead/Context/LeadContext';
import { useRelationships } from 'modules/lead/hooks/useRelationships';
import { getNestedProperty, handleRedirection } from 'utils/utils';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import { useHistory } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { deleteRelation } from 'services/relationServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

export default function Relationship(props) {
  const { control, errors, watch } = props;
  const { businessRole, relationRole } = useLeadContext();
  const { fields, append, removeItem, remove, results, loading, searchAsync } = useRelationships(control, watch);
  const [showModal, setshowModal] = useState(false);
  const { history } = useHistory();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(null);

  const itemTemplate = (e) => generateFullName(e);

  const RequiredValidation = ({ field, message = 'Field Required' }) => {
    return getNestedProperty(errors, field) ? (
      <InputErrorMessage>{getNestedProperty(errors, field)?.message || message}</InputErrorMessage>
    ) : null;
  };

  const deleteRelationInDetailPage = (id, data) => {
    let sk = data.sk ? data.sk.replaceAll('#', '%23') : '';
    let pk = data.pk ? data.pk.replaceAll('#', '%23') : '';
    deleteRelation(pk, sk)
      .then(() => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DELETE_RELATION_SUCCESS);
        setData(null);
        setIndex(null);
      })
      .catch((err) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.DELETE_RELATION_FAILURE);
      });
    remove(index);
  };

  return (
    <div className="col-12 col-md-6 search-relation">
      <h5 className="caption-bold">RELATIONSHIPS</h5>
      <div className="">
        <AutoComplete
          placeholder="Search Existing Contacts"
          field="first_name"
          className="w-100 bg-white input-shadow mb-3 p-inputtext"
          multiple
          value={fields}
          suggestions={results}
          completeMethod={searchAsync}
          loading={loading}
          itemTemplate={itemTemplate}
          selectedItemTemplate={itemTemplate}
          onSelect={(e) => append({ ...(e.value || {}), role: undefined })}
          onUnselect={(e) => removeItem(e.value)}
        />
      </div>
      <div className="card-bg lead-data-box p-3 datatable-responsive">
        <DataTable value={fields} emptyMessage="No relationships added yet" responsiveLayout="scroll" className="p-datatable-responsive">
          <Column
            header="Name"
            body={(relation) => {
              const { contact_id, sk, pk } = relation;
              const route = contact_id ? `/contacts/${contact_id}` : '/contacts';
              return (
                <>
                  <div className="p-column-title">Name</div>

                  <div
                    className="d-flex align-items-center cursor-pointer"
                    onClick={() => {
                      handleRedirection(history, route);
                      handleSettingSkAndPkToLocalStorage(sk, pk);
                    }}
                  >
                    <ImageComponent
                      filePath={relation?.profile_image}
                      fileName={relation?.contact_type !== 'person' ? relation?.title : relation?.first_name}
                      fileSize="small.jpg"
                    />
                    <div className="ms-1 redirection-link-no-text-transform">{generateFullName(relation)}</div>
                  </div>
                </>
              );
            }}
          />
          <Column
            header={
              <>
                Role<span className="danger-text"> *</span>
              </>
            }
            body={(relation, options) => {
              const index = options.rowIndex;
              return (
                <>
                  <div className="p-column-title">Role</div>
                  {relation?.contact_type === 'business' || PI_PROVIDERS_TYPE.includes(relation?.contact_type) ? (
                    <Controller
                      name={`relations.${index}.role`}
                      control={control}
                      rules={{ required: 'Role is required' }}
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          onChange={(e) => field.onChange(e.value)}
                          value={field.value}
                          options={businessRole}
                          optionLabel="name"
                          optionValue="business_role_id"
                          placeholder="Select"
                          className="w-100"
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name={`relations.${index}.role`}
                      control={control}
                      rules={{ required: 'Role is required' }}
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          onChange={(e) => field.onChange(e.value)}
                          value={field.value}
                          options={relationRole}
                          optionLabel="relation_role"
                          optionValue="relation_role_id"
                          placeholder="Select"
                          className="w-100"
                        />
                      )}
                    />
                  )}
                  <RequiredValidation field={`relations.${index}.role`} />
                </>
              );
            }}
          />
          <Column
            header="Enable Lawft Access"
            body={(relation, options) => {
              const index = options.rowIndex;
              return (
                <>
                  <div className="p-column-title">Enable Lawft Access</div>
                  <Controller
                    name={`relations.${index}.is_create_client_portal`}
                    control={control}
                    render={({ field }) => (
                      <InputSwitch
                        className="input-swich"
                        inputId={field.name}
                        onChange={(e) => relation?.email && field.onChange(e.value)}
                        checked={field.value}
                        tooltip={relation?.email ? '' : 'Add Email To Enable Lawft Access'}
                        tooltipOptions={{ position: 'bottom', className: 'case-tooltip' }}
                      />
                    )}
                  />
                </>
              );
            }}
          />
          <Column
            header="Action"
            body={(relation, options) => {
              const index = options.rowIndex;
              return (
                <>
                  <div className="p-column-title">Action</div>
                  <i
                    className="icon-delete icon-red cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDeleteConfirmation(true);
                      setData(relation);
                      setIndex(index);
                    }}
                  ></i>
                </>
              );
            }}
          />
        </DataTable>

        <div className="mt-3">
          <div className="d-flex align-items-center">
            <div className="col-sm-6 d-flex addfield mt-2" onClick={() => setshowModal(true)}>
              <i className="icon-add"></i>
              <span>Add Relationship</span>
            </div>
          </div>
        </div>

        {showModal && <AddContact show setShow={() => setshowModal(false)} handleSubmitData={(v) => append(v)} isFromAddLead={true} />}

        {showDeleteConfirmation ? (
          <ConfirmDialog
            visible={showDeleteConfirmation}
            rejectClassName="p-button-secondary outline"
            onHide={() => setShowDeleteConfirmation(false)}
            message="Are you sure you want to delete?"
            header="Confirmation"
            icon="pi pi-exclamation-triangle"
            className="bottom-radius common-popup-style"
            accept={() => {
              deleteRelationInDetailPage(index, data);
            }}
            reject={() => {
              setShowDeleteConfirmation(false);
              setData(null);
              setIndex(null);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
